import { Button } from "@/components/generic/Button";
import { createLink, LinkOptions, useMatchRoute } from "@tanstack/react-router";
import { cva } from "cva";
import { ChevronDown, ChevronUp, Lock } from "lucide-react";
import { useEffect } from "react";
import { Button as RACButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Wrap } from "../Wrap";

const ButtonLink = createLink(RACButton);

export type MenuProps = {
  items: {
    name: string;
    description: React.ReactNode;
    to: LinkOptions["to"];
    exact?: boolean;
    locked?: boolean;
    onLockedClick?: () => void;
    submenu?: {
      name: string;
      to: LinkOptions["to"];
      locked?: boolean;
      onLockedClick?: () => void;
    }[];
  }[];
};

const styles = cva({
  base: "group hover:bg-theme-on-background-hover cursor-pointer rounded bg-transparent text-left transition-colors",
  variants: {
    type: {
      menu: "relative flex items-center gap-x-4 px-4 py-3",
      submenu:
        "p-2 flex items-center justify-between text-xs leading-4 font-semibold",
    },
  },
});

export function Menu({ items }: MenuProps) {
  const matchRoute = useMatchRoute();

  return (
    <div className="flex flex-col gap-2">
      {items.map((i, idx) => {
        const hasSubmenu = !!(i.submenu && i.submenu.length > 0);

        // Check if the parent item itself is active (fuzzy for visibility)
        const isParentActive = !!matchRoute({ to: i.to, fuzzy: true });

        // Check if any submenu item is active (fuzzy match)
        const isAnySubmenuActive = hasSubmenu
          ? i.submenu!.some((s) => !!matchRoute({ to: s.to, fuzzy: true }))
          : false;

        // Submenu is visible when the parent or any submenu child is active
        const isSubmenuVisible =
          hasSubmenu && (isParentActive || isAnySubmenuActive);

        return (
          <div key={`menu-${idx}`} className="relative flex flex-col gap-2">
            <Wrap
              renderWrapper={(renderChildren) =>
                i.locked ? (
                  <RACButton
                    className={styles({ type: "menu" })}
                    onPress={i.onLockedClick}
                  >
                    {renderChildren({ isActive: false })}
                  </RACButton>
                ) : (
                  <ButtonLink
                    preload="intent"
                    className={styles({ type: "menu" })}
                    to={i.to}
                    activeOptions={{
                      exact: i.exact ?? hasSubmenu,
                      includeSearch: false,
                    }}
                  >
                    {({ isActive }) => renderChildren({ isActive })}
                  </ButtonLink>
                )
              }
              renderChildren={({ isActive }: { isActive: boolean }) => (
                <>
                  <div className="flex grow flex-col gap-y-1">
                    <div
                      className={twMerge(
                        "text-theme-on-background text-xs/4 font-semibold",
                        isActive && "text-theme-active",
                        i.locked && "text-theme-on-background-variant",
                      )}
                    >
                      {i.name}
                    </div>

                    <div className="text-theme-on-background-variant text-[10px]/4 font-semibold">
                      {i.description}
                    </div>
                  </div>

                  {i.locked ? (
                    <Lock
                      size={16}
                      className="text-theme-on-background opacity-50 transition-opacity group-hover:opacity-100"
                    />
                  ) : (
                    hasSubmenu &&
                    (isSubmenuVisible ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    ))
                  )}
                </>
              )}
            />
            {hasSubmenu && isSubmenuVisible && (
              <div className="mx-4 flex flex-col gap-y-2">
                {i.submenu?.map((s, sIdx) => (
                  <Wrap
                    key={`submenu-${sIdx}`}
                    renderWrapper={(renderChildren) =>
                      s.locked ? (
                        <RACButton
                          className={styles({ type: "submenu" })}
                          onPress={s.onLockedClick}
                        >
                          {renderChildren({ isActive: false })}
                        </RACButton>
                      ) : (
                        <ButtonLink
                          preload="intent"
                          className={styles({ type: "submenu" })}
                          to={s.to}
                          isDisabled={s.locked}
                          activeOptions={{ exact: false, includeSearch: false }}
                        >
                          {({ isActive }) => renderChildren({ isActive })}
                        </ButtonLink>
                      )
                    }
                    renderChildren={({ isActive }: { isActive: boolean }) => (
                      <>
                        <span
                          className={twMerge(
                            "relative block",
                            isActive && !s.locked && "text-theme-active",
                            s.locked && "text-theme-on-background-variant",
                          )}
                        >
                          {s.name}
                        </span>
                        {s.locked && (
                          <Lock
                            size={16}
                            className="text-theme-on-background opacity-50 transition-opacity group-hover:opacity-100"
                          />
                        )}
                      </>
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
